import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js";
import classnames from "classnames";
import equal from "fast-deep-equal";

import { IOrganization } from "@formant/core/model/IOrganization";
import { IUsageCost } from "@formant/core/model/IUsageCost";
import cssColors from "@formant/ui/style/colorExports.scss";

import { formatCentsToDollarString } from "../../../utils/formatCentsToDollarString";
import { Heading2 } from "../../common/Heading2";
import { StandardButton } from "../../common/StandardButton";
import * as styles from "./index.scss";
import { SetBudgetPopup } from "./SetBudgetPopup";

interface IDoughnutChartProps {
    usageData: IUsageCost | undefined;
    totalUsage: number;
    organization: IOrganization;
    isOverBudget: boolean;
    alertAmount: number;
    isAlertEnabled: boolean;
    isCurrentDate: boolean;
}

export const DoughnutChart = React.memo(
    function DoughnutChart({
        usageData,
        totalUsage,
        organization,
        isOverBudget,
        alertAmount,
        isAlertEnabled,
        isCurrentDate,
    }: IDoughnutChartProps) {
        const availableBudget = Math.abs(alertAmount - totalUsage);

        const chartData = useMemo(
            () => getDoughnutChartData(usageData, isCurrentDate, alertAmount),
            [usageData, getDoughnutChartData, alertAmount, isCurrentDate],
        );

        const chartRef = useRef<HTMLCanvasElement | null>(null);

        const [isModalVisible, setIsModalVisible] = useState(false);

        useEffect(() => {
            if (!chartRef?.current) {
                return;
            }

            const chartToDraw = chartRef.current.getContext("2d");

            if (!chartToDraw) {
                return;
            }

            const chart = new Chart(chartToDraw, {
                type: "doughnut",
                data: {
                    labels: ["Ingestion", "Queries", "Storage", "Available"],
                    datasets: [
                        {
                            label: "My First Dataset",
                            data: chartData,
                            backgroundColor: [
                                cssColors.errorColor,
                                cssColors.terminalBrightGreen,
                                cssColors.terminalBlue,
                                cssColors.groundColor,
                            ],
                            borderColor: "none",
                            borderWidth: 0.1,
                        },
                    ],
                },
                options: {
                    legend: { display: false },
                    cutoutPercentage: 90,
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem, tooltipData) =>
                                tooltipData.labels?.length &&
                                tooltipItem.index != null
                                    ? `${tooltipData.labels[tooltipItem.index]}`
                                    : "",
                        },
                    },
                },
            });

            // Cleanup chart to avoid issues between re renders
            return () => {
                chart.destroy();
            };
        }, [chartData]);

        return (
            <div>
                <div className={styles["doghnut-chart-container"]}>
                    <div className={styles["doghnut-chart-content"]}>
                        {isCurrentDate ? (
                            <>
                                {isAlertEnabled ? (
                                    <Heading2>{`${formatCentsToDollarString(
                                        availableBudget,
                                    )} ${
                                        isOverBudget ? "overspent" : "remaining"
                                    }`}</Heading2>
                                ) : (
                                    <p
                                        className={
                                            styles["doghnut-chart-text1"]
                                        }
                                    >
                                        Don&apos;t like surprise bills?
                                    </p>
                                )}

                                <p
                                    className={classnames(
                                        styles["doghnut-chart-text2"],
                                        isAlertEnabled
                                            ? styles["dark-text"]
                                            : "",
                                    )}
                                >
                                    {isAlertEnabled
                                        ? `${formatCentsToDollarString(
                                              alertAmount,
                                          )} budgeted`
                                        : `Use alerts to avoid overspending.`}
                                </p>
                                <StandardButton
                                    className={styles["alert-button"]}
                                    dark={isAlertEnabled}
                                    onClick={() => setIsModalVisible(true)}
                                >
                                    {isAlertEnabled
                                        ? "Alert settings..."
                                        : "Use alerts..."}
                                </StandardButton>
                            </>
                        ) : null}

                        {!usageData && !isCurrentDate ? (
                            <p
                                className={classnames(
                                    styles["doghnut-chart-text2"],
                                    styles["dark-text"],
                                )}
                            >
                                No usage data
                            </p>
                        ) : null}

                        {usageData && !isCurrentDate ? (
                            <Heading2>
                                {`${formatCentsToDollarString(
                                    totalUsage,
                                )} spent`}
                            </Heading2>
                        ) : null}
                    </div>

                    <canvas
                        height="100%"
                        width="100%"
                        id="myChart"
                        ref={chartRef}
                    />
                </div>
                <SetBudgetPopup
                    organization={organization}
                    popupProps={{
                        visible: isModalVisible,
                        onClose() {
                            setIsModalVisible(false);
                        },
                    }}
                />
            </div>
        );
    },
    (prevProps, nextProps) => {
        return (
            equal(prevProps.organization, nextProps.organization) &&
            equal(prevProps.usageData, nextProps.usageData) &&
            equal(prevProps.isCurrentDate, nextProps.isCurrentDate)
        );
    },
);

function getDoughnutChartData(
    usageData: IUsageCost | undefined,
    isCurrentDate: boolean,
    alertAmount = 0,
) {
    let ingestionCost = 0;
    let queryCost = 0;
    let storageCost = 0;
    // if we are not on the current date alert amount wont be displayed on the chart
    let totalCost = isCurrentDate ? alertAmount : 0;

    if (usageData && usageData?.costItems.length > 0) {
        usageData.costItems.forEach(current => {
            if (current.type === "data-in-bytes") {
                // ingestion
                ingestionCost += current.costUsdCents;
                totalCost -= current.costUsdCents;
            } else if (current.type === "data-out-bytes") {
                // query
                queryCost += current.costUsdCents;
                totalCost -= current.costUsdCents;
            } else if (current.type === "data-stored-bytes") {
                // storage
                storageCost += current.costUsdCents;
                totalCost -= current.costUsdCents;
            }
        });
    } else {
        // If no costItems exist,we are on a period were the user was not billed we set the total to 100 to display the chart
        totalCost = 100;
    }
    // If total value on chart is negative it means that we are overbudget
    // so we set the value to 0 to indicate that theres no available amount
    if (totalCost <= 0) {
        totalCost = 0;
    }

    // If billing period didnt have any cost items that generated some usage we set the total to 100 to display the chart
    if (ingestionCost === 0 && queryCost === 0 && storageCost === 0) {
        totalCost = 100;
    }

    return [ingestionCost, queryCost, storageCost, totalCost];
}
